import { useContext } from 'react';
import { notificationsContext } from './notificationsContext';
import { NotificationType } from './notificationModel';

/**
 * Hook to push app-wide toast notifications through the global
 * `NotificationsStack`.
 *
 * NOTE ON NAMING: this is the legacy "toast stack" notifier. The name
 * `useNotifications` is already taken in this repo by the NotificationBell's
 * React-Query hook (`src/modules/Notifications/hooks/useNotifications.js`),
 * so this system exposes `useNotifier` instead to avoid the collision.
 *
 * @example
 * import { useNotifier } from 'common/components/NotificationsStack';
 * const MyComponent = () => {
 *   const { successNotif, dangerNotif } = useNotifier();
 *   return (
 *     <button onClick={() => successNotif('Saved!', 'optional description')}>
 *       Save
 *     </button>
 *   );
 * };
 *
 * @returns {{
 *   pushNotif: (payload: import('./notification').NotificationPayload) => void,
 *   successNotif: (message: React.ReactNode, description?: React.ReactNode) => void,
 *   dangerNotif: (message: React.ReactNode, description?: React.ReactNode) => void,
 * }}
 * @throws When used outside a `<NotificationsProvider>`.
 */
export const useNotifier = () => {
    const context = useContext(notificationsContext);

    if (context === undefined) {
        throw new Error(
            'useNotifier must be used within a <NotificationsProvider>. ' +
                'Did you forget to set up <NotificationsProvider> in App.js?'
        );
    }

    const { pushNotif } = context;

    return {
        pushNotif,
        successNotif: (message, description) =>
            pushNotif({ message, description, type: NotificationType.Success }),
        dangerNotif: (message, description) =>
            pushNotif({ message, description, type: NotificationType.Danger }),
    };
};

export default useNotifier;